import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../provider/AuthProvider';
import api, { setAuthToken } from '../../api/api';
import { sanitizeAiHtml } from '../../utils/sanitize';  // OWASP A03 — XSS prevention
import './AIChatOverlay.css';

const AIChatOverlay = () => {
    const { token } = useAuth();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: 1, type: 'bot',
            text: 'Hi! I am your **CX Assistant**, powered by Nexus AI. I can search your CRM, review helpdesk tickets, and take actions on your behalf.\n\nWhat would you like to know?',
            timestamp: new Date(),
        }
    ]);
    const [inputText, setInputText] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [conversationHistory, setConversationHistory] = useState([]);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isThinking, scrollToBottom]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    const toggleChat = () => setIsOpen(v => !v);

    // The AI home page is the assistant itself; no floating widget on top of it.
    if (location.pathname.startsWith('/home')) return null;

    // Convert AI markdown to clean readable HTML for the chat widget
    const formatText = (text) => {
        if (!text) return '';

        return text
            // Strip ### ## # headings — turn into plain bold text on its own line
            .replace(/^#{1,3}\s+(.+)$/gm, '<strong>$1</strong>')
            // Convert **bold** to <strong>
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            // Convert *italic* to <em>
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            // Strip horizontal rules ---
            .replace(/^---+$/gm, '')
            // Convert "- item" bullet lines to styled bullets
            .replace(/^[-•]\s+(.+)$/gm, '<span class="chat-bullet">›</span> $1')
            // Convert newlines to <br/>
            .replace(/\n/g, '<br/>');
    };

    const handleSend = async () => {
        if (!inputText.trim() || isThinking) return;

        const userText = inputText.trim();
        const userMsg = {
            id: Date.now(), type: 'user',
            text: userText, timestamp: new Date()
        };

        setMessages(prev => [...prev, userMsg]);
        setInputText('');
        setIsThinking(true);

        // Update conversation history for context memory
        const updatedHistory = [
            ...conversationHistory,
            { role: 'user', content: userText }
        ].slice(-10); // Keep last 10 exchanges

        try {
            setAuthToken(token);
            const response = await api.post('/nexus/chat/', {
                message: userText,
                conversation_history: updatedHistory,
            });

            const data = response.data;

            const botMsg = {
                id: Date.now() + 1,
                type: 'bot',
                text: data.response || "I wasn't able to complete that request.",
                timestamp: new Date(),
                runId: data.run_id,
                toolCalls: data.tool_calls || 0,
                steps: data.steps || [],
                status: data.status,
            };

            setMessages(prev => [...prev, botMsg]);

            // Update conversation history with bot reply
            setConversationHistory([
                ...updatedHistory,
                { role: 'assistant', content: data.response || '' }
            ].slice(-10));

        } catch (error) {
            console.error('NEXUS Chat error:', error);
            const errMsg = error.response?.data?.response || error.response?.data?.error || error.message;
            setMessages(prev => [...prev, {
                id: Date.now() + 1, type: 'bot',
                text: `⚠️ ${errMsg || 'Something went wrong. Please try again.'}`,
                timestamp: new Date(), isError: true,
            }]);
        } finally {
            setIsThinking(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const QUICK_PROMPTS = [
        { label: '📊 Ticket Summary', text: 'Give me a summary of all helpdesk tickets' },
        { label: '🔍 Find Customer', text: 'Search for a customer named Acme' },
        { label: '⚠️ Urgent Issues', text: 'List all urgent priority tickets' },
        { label: '📈 Pipeline', text: 'Show me the current sales pipeline deals' },
    ];

    return (
        <div className={`ai-chat-container ${isOpen ? 'open' : ''}`}>
            {/* Floating Action Button */}
            <button
                className="ai-fab"
                onClick={toggleChat}
                title={isOpen ? 'Close CX Assistant' : 'Open CX Assistant'}
            >
                {isOpen
                    ? <i className="fas fa-times" />
                    : <i className="fas fa-robot" />
                }
                {!isOpen && <span className="fab-pulse" />}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div className="ai-chat-window">
                    {/* Header */}
                    <div className="ai-chat-header">
                        <div className="ai-header-info">
                            <div className="ai-avatar nexus-avatar">
                                <i className="fas fa-robot" />
                            </div>
                            <div>
                                <h3>CX Assistant</h3>
                                <div className="ai-header-meta">
                                    <span className="status-dot online" />
                                    <small>Powered by Nexus AI</small>
                                </div>
                            </div>
                        </div>
                        <button className="close-btn" onClick={toggleChat}>
                            <i className="fas fa-minus" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="ai-chat-body">
                        {messages.map((msg) => (
                            <div key={msg.id} className={`message ${msg.type} ${msg.isError ? 'error' : ''}`}>
                                {msg.type === 'bot' && (
                                    <div className="msg-avatar nexus-msg-avatar">
                                        <i className="fas fa-robot" />
                                    </div>
                                )}
                                <div className="message-content">
                                    {/* sanitizeAiHtml strips links/images from AI output — OWASP A03 */}
                                    <p dangerouslySetInnerHTML={{ __html: sanitizeAiHtml(formatText(msg.text)) }} />

                                    <span className="timestamp">
                                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            </div>
                        ))}

                        {/* Thinking State */}
                        {isThinking && (
                            <div className="message bot thinking">
                                <div className="msg-avatar nexus-msg-avatar">
                                    <i className="fas fa-robot" />
                                </div>
                                <div className="message-content thinking-content">
                                    <div className="typing-indicator">
                                        <span /><span /><span />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quick prompts (shown only when empty) */}
                        {messages.length === 1 && !isThinking && (
                            <div className="quick-prompts">
                                <p className="quick-prompts-label">Try asking:</p>
                                <div className="quick-prompts-grid">
                                    {QUICK_PROMPTS.map((qp) => (
                                        <button
                                            key={qp.text}
                                            className="quick-prompt-btn"
                                            onClick={() => { setInputText(qp.text); inputRef.current?.focus(); }}
                                        >
                                            {qp.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Footer */}
                    <div className="ai-chat-footer">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Ask about tickets, customers, deals..."
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isThinking}
                        />
                        <button
                            className={`send-btn ${isThinking ? 'disabled' : ''}`}
                            onClick={handleSend}
                            disabled={isThinking || !inputText.trim()}
                        >
                            {isThinking
                                ? <i className="fas fa-circle-notch fa-spin" />
                                : <i className="fas fa-paper-plane" />
                            }
                        </button>
                    </div>

                    <div className="nexus-powered-footer">
                        ⚡ Powered by NEXUS Agentic AI
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIChatOverlay;
